<template>
  <n-card>
    <new-site-dialog
      @close="isNewSiteDialogOpened = false"
      @created="newSiteCreated"
      :client-id="client.id"
      :maker-id="makerId"
      :default-name="searchString"
      v-if="isNewSiteDialogOpened"
    />
    <template v-if="!isFetchingSites">
      <v-card-text>
        <v-row no-gutters justify="center" align="center">
          <v-col>
            <v-row no-gutters justify="center" align="center">
              <div>
                <v-row no-gutters justify="center" align="center" class="border-component mr-3" style="height: 40px;">
                  <v-autocomplete
                    :items="tags"
                    v-model="searchTags"
                    single-line
                    solo
                    label="Tous les tags"
                    hide-details
                    item-text="label"
                    item-value="id"
                    no-data-text="Aucun tag"
                    clearable
                    dense
                    flat
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    style="width: 100%;"
                    class="n-multi-select input-no-b-line"
                  >
                    <template v-slot:item="{ item: tag }">
                      <v-list-item-avatar size="20" style="margin-top: 4px;">
                        <v-avatar :color="tag.color"></v-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        {{ tag.label }}
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-row>
              </div>
              <v-col>
                <v-text-field
                  v-model="searchString"
                  placeholder="Rechercher un site..."
                  prepend-inner-icon="search"
                  hide-details
                  dense
                  clearable
                  outlined
                ></v-text-field>
              </v-col>
              <v-btn :disabled="!$canWrite" dark color="primary" elevation="0" class="ml-4" @click="$canWrite && (isNewSiteDialogOpened = true)" v-if="canCreateSite">
                <v-icon left>add</v-icon>
                Nouveau
              </v-btn>
            </v-row>
            <div class="pt-4 pb-2">
              <v-row no-gutters justify="center" align="center">
                <h3 class="pl-2">{{ filteredSites.length }} {{ `site` | pluralize(filteredSites.length) }} {{ `trouvé` | pluralize(filteredSites.length) }}</h3>
                <v-spacer />
                <div>
                  <v-row no-gutters justify="center" align="center">
                    <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text" @click="previousPage">
                      <v-icon>chevron_left</v-icon>
                    </v-btn>
                    <div class="ml-2 primary--text font-weight-bold">
                      {{ currentPage + 1 }} / {{ nbPages || 1 }}
                    </div>
                    <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text ml-2" @click="nextPage">
                      <v-icon>chevron_right</v-icon>
                    </v-btn>
                  </v-row>
                </div>
              </v-row>
            </div>
            <v-row no-gutters justify="center" align="center" class="pa-4 text--secondary" style="min-height: 100px;" v-if="!filteredSites.length">
              Aucun résultat
            </v-row>
            <n-card depressed v-for="site in filteredSitesPaginated" :key="site.id" @click="chooseSite(site)" class="my-4 clickable">
              <v-row no-gutters justify="center" align="center">
                <v-col class="py-2 px-4">
                  <v-row no-gutters justify="center" align="center">
                    <div class="font-weight-bold">
                      {{ site.$label }}
                    </div>
                    <v-spacer />
                    <div class="font-italic pl-2" v-if="site.$tenant.length">
                      {{ site.$tenant }}
                      <v-icon small class="ml-1">person</v-icon>
                    </div>
                    <div class="font-italic pl-2" v-if="site.$owner.length">
                      {{ site.$owner }}
                      <v-icon small class="ml-1">badge</v-icon>
                    </div>
                  </v-row>
                  <div v-if="site.address">{{ site.address }}</div>
                  <div>
                    <span class="pr-2" v-if="site.postalCode">{{ site.postalCode }}</span>
                    <span v-if="site.city">{{ site.city }}</span>
                  </div>
                  <div v-if="site.$displayedTags.length">
                    <tag-chip v-for="tag in site.$displayedTags" :key="`tag-${tag.id}`" :tag="tag" />
                  </div>
                </v-col>
                <v-divider vertical />
                <div class="py-2 px-4">
                  <v-icon>chevron_right</v-icon>
                </div>
              </v-row>
            </n-card>
            <div class="pa-4" v-if="filteredSites.length">
              <v-row no-gutters justify="center" align="center">
                <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text" @click="previousPage">
                  <v-icon>chevron_left</v-icon>
                </v-btn>
                <div class="ml-2 primary--text font-weight-bold">
                  {{ currentPage + 1 }} / {{ nbPages || 1 }}
                </div>
                <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text ml-2" @click="nextPage">
                  <v-icon>chevron_right</v-icon>
                </v-btn>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

    </template>
    <template v-else>
      <v-row no-gutters justify="center" align="center" class="my-5">
        <v-progress-circular indeterminate color="primary darken-1"></v-progress-circular>
      </v-row>
    </template>
  </n-card>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapState } from 'vuex';
import NewSiteDialog from '@/components/NewSiteModal.vue';

const PAGE_SIZE = 25;

export default {
  data: () => ({
    selectedSiteId: null,
    isSubmiting: false,
    searchString: ``,
    searchTags: [],
    isFetchingSites: false,
    isNewSiteDialogOpened: false,
    currentPage: 0,
  }),
  computed: {
    flavoredSites() {
      const displayClientTags = this.isAggregatorUser;
      const displayMakerTags = !displayClientTags && this.isMakerUser && this.isMaker;
      return _.chain(this.sites)
        .map(site => {
          const clientTags = site.clientTags || [];
          const makerTags = site.makerTags || [];
          const tagsLabels = [];
          if (displayClientTags) {
            tagsLabels.push(..._.map(clientTags, `label`));
          } else if (displayMakerTags) {
            tagsLabels.push(..._.map(makerTags, `label`));
          }
          const tagsLabelsStr = tagsLabels.join(` `);
          const $refLabel = site.ref && site.ref.length ? ` (Ref: ${site.ref})` : ``;
          const $label = `${site.name}${$refLabel}`;
          return {
            ...site,
            $label,
            $name: _.toLower($label),
            $searchString: _.toLower(`
              ${$label} ${site.address || ``} ${site.postalCode || ``} ${site.city || ``} ${site.tenant || ``} ${site.owner || ``} ${site.Zelok_id || ``} ${tagsLabelsStr}
            `),
            $clientTags: clientTags,
            $makerTags: makerTags,
            $tenant: this.isRealEstateClient ? _.trim(site.tenant) || `` : ``,
            $owner: this.isRealEstateClient ? _.trim(site.owner) || `` : ``,
            $refLabel,
          };
        })
        .map(site => ({
          ...site,
          $clientTagsIds: _.map(site.$clientTags || [], `id`),
          $makerTagsIds: _.map(site.$makerTags || [], `id`),
        }))
        .map(site => ({
          ...site,
          $displayedTags: displayClientTags ? site.$clientTags : (displayMakerTags ? site.$makerTags : []), // eslint-disable-line
        }))
        .orderBy(`$name`)
        .value();
    },
    filteredSites() {
      let sites = this.flavoredSites;
      if (_.trim(this.searchString) !== ``) {
        this.searchString.split(` `).forEach(searchStringPart => {
          searchStringPart = _.toLower(searchStringPart);
          sites = _.filter(sites, site =>
            site.$searchString.indexOf(searchStringPart) !== -1
          );
        });
      }
      if (this.searchTags.length) {
        const tagsIdsProperty = this.isAggregatorUser ? `$clientTagsIds` : `$makerTagsIds`;
        sites = _.filter(sites, site =>
          _.without(this.searchTags, ...site[tagsIdsProperty]).length === 0
        );
      }
      return sites;
    },
    filteredSitesPaginated() {
      const firstIndex = this.currentPage * PAGE_SIZE;
      return this.filteredSites.slice(firstIndex, firstIndex + PAGE_SIZE);
    },
    nbPages() {
      return Math.ceil(this.filteredSites.length / PAGE_SIZE);
    },
    ...mapState(`model/user`, [
      `isAggregatorUser`,
      `isMakerUser`,
    ]),
    ...mapState(`model/client`, [
      `company`,
      `isMaker`,
    ]),
    ...mapState(`ui/newIntervention`, [
      `tags`,
      `client`,
      `site`,
    ]),
    ...mapGetters(`ui/newIntervention`, [
      `sites`,
      `isAggregatorClient`,
      `isRealEstateClient`,
      `firstMaker`,
    ]),
    isOnAggreagator() {
      return Boolean(this.isAggregatorClient && this.isAggregatorUser);
    },
    canCreateSite() {
      return Boolean(
        this.company
        && (this.isOnAggreagator || (!this.isAggregatorClient && this.isMaker))
        && [`aba`, `sushi-robots`, `techni-froid-cuisines`].indexOf(this.company.slug) === -1
      );
    },
    makerId() {
      if (this.isOnAggreagator) {
        return null;
      }

      return this.firstMaker?.id || null;
    },
  },
  methods: {
    async fetchSites() {
      this.isFetchingSites = true;
      await this.$store.dispatch(`ui/newIntervention/fetchSites`);
      this.isFetchingSites = false;
      this.$emit(`ready`);
    },
    previousPage() {
      if (this.currentPage > 0) {
        this.currentPage -= 1;
      }
    },
    nextPage() {
      const newPageSiteIndex = (this.currentPage + 1) * PAGE_SIZE;
      if (newPageSiteIndex < this.filteredSites.length) {
        this.currentPage += 1;
      }
    },
    chooseSite(site) {
      this.selectedSiteId = site.id;
      return this.submit();
    },
    async newSiteCreated({ id }) {
      this.isNewSiteDialogOpened = false;
      await this.fetchSites();
      const site = _.find(this.flavoredSites, { id });
      this.chooseSite(site);
    },
    submit() {
      if (this.isSubmiting) { return; }
      this.isSubmiting = true;
      const site = _.find(this.sites, { id: this.selectedSiteId });
      this.$store.commit(`ui/newIntervention/SET_SITE`, site);
      this.$emit(`choose`, site);
    },
  },
  watch: {
    searchString() {
      this.currentPage = 0;
    },
    searchTags() {
      this.currentPage = 0;
    },
  },
  async beforeMount() {
    this.currentPage = 0;
    await this.fetchSites();
    if (this.site && this.site.id) {
      const site = _.find(this.sites, { id: this.site.id });
      if (site) {
        this.selectedSiteId = site.id;
        return this.submit();
      }
    }
    if (this.sites.length === 1 && this.client.isMonoSite) {
      this.selectedSiteId = this.sites[0].id;
      this.submit();
    }

    return Promise.resolve();
  },
  components: {
    NewSiteDialog,
  },
};
</script>
